import { ReactNode } from 'react';
import { IPhoneMockup as IPhoneMockupFrame } from 'react-device-mockup';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { IPhoneStatusBar } from '@/pages/onboarding/mockup/iphone-status-bar';

export interface IPhoneMockupProps {
  /**
   * Identifies the scene currently rendered as `children`. Changing it
   * triggers the cross-fade transition between scenes.
   */
  sceneKey: string;
  theme?: 'light' | 'dark';
  screenWidth?: number;
  /**
   * True when `children` already draws its own status bar and home
   * indicator (e.g. HomeScreenScene's wallpaper is a pixel export of the
   * full Figma frame, chrome included) — skips our own status bar overlay
   * and home indicator so they don't double up or drift out of sync with
   * whatever the scene baked in.
   */
  chromeBaked?: boolean;
  children: ReactNode;
}

// react-device-mockup renders the whole device chrome (bezel, buttons,
// Dynamic Island, home indicator) in a single `frameColor`, so a near-black
// tone keeps the Dynamic Island reading as a real cutout instead of the
// library's default mid-gray pill, matching the Figma iPhone frame.
const FRAME_COLOR = '#1c1c1e';

// iOS renders its own chrome (status bar, home indicator, app names on the
// home screen) in the system font, never the host app's brand font. Without
// this, every text node in the mockup inherits the admin dashboard's
// "Plus Jakarta Sans", which reads as visibly wrong against the Figma
// reference (Templates Korza, node 1338:12358).
const IOS_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif';

// react-device-mockup only rounds+clips its *outer* frame (the bezel, via a
// border-radius + overflow:hidden on the element carrying the frame border)
// — the screen content slot it hands us (where `children` renders) is a
// plain rectangle, clipped only in the sense of also having overflow:hidden,
// and relies entirely on sitting inside that rounded frame to *look*
// rounded. That nested "nothing's own shape matches the phone, it's just
// cropped by an ancestor" setup is exactly what tends to glitch under an
// animated `transform` on an ancestor (the frame's rounded clip can
// momentarily desync from its content, flashing square corners) — so we
// give our own screen wrapper the matching corner radius too, computed the
// same way react-device-mockup derives its own `bezelRadius`/`frameWidth`
// for the "island" variant (see its IPhoneIslandPortrait/Landscape), minus
// the frame's border width (the standard nested-radius formula: inner
// radius = outer radius - border width). That way the content is actually
// shaped like the phone's screen, not just cropped by one.
export function getScreenCornerRadius(screenWidth: number): number {
  const getSizeWithRatio = (size: number) =>
    Math.max(Math.floor((screenWidth * size) / 390), 1);
  const bezelRadius = getSizeWithRatio(68);
  const frameWidth = getSizeWithRatio(10);
  return Math.max(bezelRadius - frameWidth, 0);
}

// Wraps react-device-mockup's iPhone frame (notch/Dynamic Island) with our
// own status bar and a scene slot. The scene's content is provided by the
// caller as `children` (SPEC_DEVIATION: design.md sketches `scene` as a
// fixed union prop, but the scene components built in T14/T16/T17 are full
// JSX trees, not renderable from a string — a children slot keeps
// IPhoneMockup decoupled from the individual scene components, matching
// T13's task description of "whichever scene is passed as children").
export function IPhoneMockup({
  sceneKey,
  theme = 'light',
  screenWidth = 300,
  chromeBaked = false,
  children,
}: IPhoneMockupProps) {
  const shouldReduceMotion = useReducedMotion();
  const screenCornerRadius = getScreenCornerRadius(screenWidth);

  return (
    <IPhoneMockupFrame
      screenWidth={screenWidth}
      screenType="island"
      hideStatusBar
      hideNavBar
      frameColor={FRAME_COLOR}
    >
      <div
        className="relative h-full w-full overflow-hidden"
        style={{
          fontFamily: IOS_FONT_STACK,
          borderRadius: screenCornerRadius,
          // Promotes this clip to its own compositing layer, independent of
          // the ancestor ZoomedIPhoneMockup's animated `scale`/`y` transform
          // (and, within it, any content re-rendering — e.g. tab-bar drag
          // reordering — while that transform is active). Without this the
          // browser can desync the rounded clip from the transformed
          // coordinate space it's nested in, flashing an unclipped square
          // corner for a frame or two — the exact glitch `screenCornerRadius`
          // above was already written to avoid, just not under this
          // combination of an active ancestor transform *and* content churn.
          transform: 'translateZ(0)',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={sceneKey}
            className="flex h-full w-full flex-col overflow-hidden"
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.4 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
        {!chromeBaked && (
          <>
            {/* Overlaid on top of the scene, not a layout sibling — iOS draws
                the status bar over the app's content (e.g. a light scene's
                background runs edge-to-edge behind it), it never pushes
                content down. */}
            <div className="pointer-events-none absolute inset-x-0 top-0">
              <IPhoneStatusBar theme={theme} />
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
              <div
                className={`h-[5px] w-[134px] rounded-full ${theme === 'dark' ? 'bg-white/90' : 'bg-black/90'}`}
              />
            </div>
          </>
        )}
      </div>
    </IPhoneMockupFrame>
  );
}
